import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Checked often enough that somebody already working finds out within a
// minute rather than by having a save refused. The call is a single setting
// read and returns almost nothing.
const POLL_MS = 60000;

/**
 * The maintenance notice, shown to everybody on every page.
 *
 * Placed in the layout rather than on the home page: somebody who is about to
 * upload a file is on a project page, and a notice they have to go looking for
 * is one they find after the refusal rather than before it.
 *
 * An administrator sees the same bar. It is the only way another
 * administrator learns that maintenance was turned on without being told, and
 * it is also the reminder that turns it off again - which is the failure this
 * feature has in practice, being left on.
 */
export default function MaintenanceBanner() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [state, setState] = useState(null);

  const check = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/maintenance`, { withCredentials: true });
      setState(data);
    } catch {
      // Not signed in, or the request failed. Showing nothing is right in
      // both cases: a banner that appears because a request failed would be
      // worse than no banner.
      setState(null);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    check();
    const timer = setInterval(() => { if (!document.hidden) check(); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, check]);

  if (!user || !state?.active) return null;

  const isAdmin = user.role === "admin";

  return (
    <div
      data-testid="maintenance-banner"
      className="sticky top-0 z-40 bg-amber-500 text-amber-950 px-4 py-2.5 shadow-sm"
    >
      {/* Who turned it on and when are recorded in the audit log and shown in
          Settings. On the bar they were noise: the person reading it needs to
          know what they can still do, not who to ask about it. */}
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <Wrench className="w-4 h-4 shrink-0" />
        <span className="font-bold">{t("maintenanceBanner.active")}</span>
        <span className="opacity-90">{t("maintenanceBanner.readOnly")}</span>
        {state.reason && <span className="opacity-90">{state.reason}</span>}
        {isAdmin && (
          <Link
            to="/settings"
            data-testid="maintenance-banner-link"
            className="ml-auto shrink-0 underline font-semibold hover:opacity-80"
          >
            {t("maintenanceBanner.manage")}
          </Link>
        )}
      </div>
      {isAdmin && (
        <div className="max-w-7xl mx-auto text-xs opacity-75 mt-0.5 pl-7">
          {t("maintenanceBanner.adminNote")}
        </div>
      )}
    </div>
  );
}
